import { useCallback } from 'react';
import {
  Documentation,
  DocumentationFilterInputObject,
  DocumentationScopeInputObject,
  FilterGroupingEnum,
  useEdmDocumentationsIdsLazyQuery,
} from 'graphql-common';
import {
  EdmDocumentationsQueryVariables,
  useEdmDocumentationsQuery,
} from '@lib/graphql-common';
import _get from 'lodash/get';
import { GetUrlParams } from '@lib/enums/urls';
import createSearchFilters from '@lib/utils/createSearchFilters';
import { ListQueryHookProps, ListQueryResultWithIds } from '../types';

type EdmDocumentationsModalQueryHookProps = ListQueryHookProps & {
  isViewLocked?: boolean;
  scope?: DocumentationScopeInputObject;
  skipCategory?: boolean;
};

export default function useEdmDocumentationsModalQueryHook({
  additionalFilters,
  ids,
  isViewLocked,
  orderingParams,
  paginationParams,
  scope,
  searchQuery,
  searchQueryFilterSchema = ['name', 'code', 'categoryName', 'tagsName'],
  skip,
  skipCategory,
  usePrevious = true,
  fetchPolicy = 'cache-and-network',
}: EdmDocumentationsModalQueryHookProps): ListQueryResultWithIds<Documentation> {
  const page = _get(paginationParams, GetUrlParams.Page);
  const perPage = _get(paginationParams, GetUrlParams.PerPage);
  const orderingField = _get(orderingParams, GetUrlParams.OrderingField);
  const orderingDirection = _get(
    orderingParams,
    GetUrlParams.OrderingDirection,
  );
  const pageNumber = page ? Number.parseInt(page, 10) : NaN;
  const limitNumber = perPage ? Number.parseInt(perPage, 10) : NaN;
  const queryVariables: EdmDocumentationsQueryVariables = {
    page: Number.isNaN(pageNumber) ? undefined : pageNumber,
    limit: Number.isNaN(limitNumber) ? undefined : limitNumber,
    scope,
    // BE-gated premium fragment / category resolver — must be skipped on a
    // plan-locked company to avoid UPGRADE_PLAN_REQUIRED.
    isViewLocked: isViewLocked ?? false,
    skipCategory: skipCategory ?? false,
  };
  const filters: DocumentationFilterInputObject[] = [];
  if (searchQuery) {
    const searchFields = searchQueryFilterSchema.map((fieldName) => ({
      fieldName,
    }));
    filters.push(...createSearchFilters(searchQuery, searchFields));
  }
  if (ids?.length) {
    filters.push({
      id: {
        grouping: FilterGroupingEnum.Or,
        predicate: {
          in: ids,
        },
        filterGroup: 'ids',
      },
    });
  }
  if (additionalFilters?.length) {
    filters.push(...additionalFilters);
  }
  if (filters.length) {
    queryVariables.filters = filters;
  }
  if (orderingField && orderingDirection) {
    queryVariables.sorts = {
      [orderingField]: orderingDirection,
    };
  }
  const query = useEdmDocumentationsQuery({
    fetchPolicy,
    variables: queryVariables,
    skip,
  });
  const [fetchIdsQuery] = useEdmDocumentationsIdsLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const fetchIds = useCallback(
    () =>
      fetchIdsQuery({
        variables: {
          onlyIds: true,
          filters: queryVariables.filters,
        },
      }),
    [fetchIdsQuery, queryVariables.filters],
  );
  const { loading, data, previousData, refetch, error } = query;
  const queryData = usePrevious ? data || previousData : data;
  const firstLoading = loading && previousData === undefined;
  const collection =
    !usePrevious && loading
      ? []
      : (_get(queryData, 'data.collection', []) as Documentation[]);
  const metadata = _get(queryData, 'data.metadata');
  const totalCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 1);

  return {
    fetchIds,
    refetch,
    collection,
    error,
    firstLoading,
    loading,
    totalPages,
    totalCount,
  };
}
